import * as Types from '@trello/graphql/generated';

import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
import process from 'process';
export type BoardSwitcherMemberFragment = (
  { __typename: 'Member' }
  & Pick<Types.Member, 'idEnterprisesAdmin' | 'idPremOrgsAdmin'>
  & { boards: Array<(
    { __typename: 'Board' }
    & Pick<Types.Board, 'id' | 'idOrganization' | 'name' | 'starred'>
    & { organization?: Types.Maybe<(
      { __typename: 'Organization' }
      & Pick<Types.Organization, 'displayName'>
    )> }
  )>, boardStars: Array<(
    { __typename: 'BoardStar' }
    & Pick<Types.BoardStar, 'id' | 'idBoard' | 'pos'>
  )>, organizations: Array<(
    { __typename: 'Organization' }
    & Pick<Types.Organization, 'id' | 'displayName' | 'idEnterprise' | 'name' | 'offering'>
  )> }
);

export const BoardSwitcherMemberFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BoardSwitcherMember' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'Member' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'boards' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ListValue',
                  values: [
                    { kind: 'EnumValue', value: 'open' },
                    { kind: 'EnumValue', value: 'starred' },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'idOrganization' },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'organization' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'displayName' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'starred' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'boardStars' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'idBoard' } },
                { kind: 'Field', name: { kind: 'Name', value: 'pos' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'idEnterprisesAdmin' },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'idPremOrgsAdmin' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'organizations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'BoardSwitcherWorkspace' },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BoardSwitcherWorkspace' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'Organization' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'idEnterprise' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offering' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

interface UseBoardSwitcherMemberFragmentOptions
  extends Omit<
    Apollo.UseFragmentOptions<
      BoardSwitcherMemberFragment,
      Apollo.OperationVariables
    >,
    'fragment' | 'fragmentName' | 'from'
  > {
  from: Apollo.StoreObject | Apollo.Reference;
  returnPartialData?: boolean;
}

interface UseBoardSwitcherMemberFragmentResult
  extends Omit<Apollo.UseFragmentResult<BoardSwitcherMemberFragment>, 'data'> {
  data?: BoardSwitcherMemberFragment;
}

export const useBoardSwitcherMemberFragment = ({
  from,
  returnPartialData,
  ...options
}: UseBoardSwitcherMemberFragmentOptions): UseBoardSwitcherMemberFragmentResult => {
  const result = Apollo.useFragment<BoardSwitcherMemberFragment>({
    ...options,
    fragment: BoardSwitcherMemberFragmentDoc,
    fragmentName: 'BoardSwitcherMember',
    from: {
      __typename: 'Member',
      ...from,
    },
  });

  // Ensure that the fragment result is not typed as a DeepPartial.
  if (!result.complete && !returnPartialData) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Fragment data is incomplete.', result);
    }
    return {
      ...result,
      data: undefined,
    };
  }

  return { ...result, data: result.data as BoardSwitcherMemberFragment };
};
